/**
 * HTTP Request Command
 * Make HTTP requests with detailed output
 */
import { Command } from 'commander';
import { readFile } from 'fs/promises';
import { UtilsService } from '../../services/utils.service.js';

async function handleHttpRequest(
    url: string,
    options: {
        method: string;
        header?: string[];
        data?: string;
        file?: string;
        timeout: number;
        noFollow: boolean;
        onlyHeaders: boolean;
        onlyBody: boolean;
    }
): Promise<void> {
    const service = new UtilsService();

    try {
        // Parse headers
        const headers: Record<string, string> = {};
        if (options.header) {
            options.header.forEach((header) => {
                const [key, ...valueParts] = header.split(':');
                if (key && valueParts.length > 0) {
                    headers[key.trim()] = valueParts.join(':').trim();
                }
            });
        }

        // Get body
        let body: string | undefined;
        if (options.file) {
            body = await readFile(options.file, 'utf-8');
        } else if (options.data) {
            body = options.data;
        }

        // Make request
        const response = await service.httpRequest(url, {
            method: options.method.toUpperCase(),
            headers,
            body,
            timeout: options.timeout,
            followRedirects: !options.noFollow,
        });

        // Display results
        if (!options.onlyBody) {
            console.log(`HTTP/${response.status >= 200 && response.status < 300 ? '1.1' : '1.0'} ${response.status} ${response.statusText}`);
            console.log('───────────────────────────────────────────');
            console.log(`📊 Status: ${response.status} ${response.statusText}`);
            console.log(`⏱️  Time: ${response.timing}ms`);
            console.log(`📦 Size: ${service.formatBytes(response.size)}`);
            console.log();
        }

        if (!options.onlyBody && !options.onlyHeaders) {
            console.log('📋 Headers:');
            Object.entries(response.headers).forEach(([key, value]) => {
                console.log(`   ${key}: ${value}`);
            });
            console.log();
        }

        if (options.onlyHeaders) {
            Object.entries(response.headers).forEach(([key, value]) => {
                console.log(`${key}: ${value}`);
            });
            return;
        }

        if (!options.onlyHeaders) {
            if (!options.onlyBody) {
                console.log('📄 Body:');
            }

            // Try to format as JSON
            try {
                const json = JSON.parse(response.body);
                console.log(JSON.stringify(json, null, 2));
            } catch {
                console.log(response.body);
            }
            console.log();
        }
    } catch (error) {
        console.error('❌ Error:', error instanceof Error ? error.message : String(error));
        process.exit(1);
    }
}

export function createHttpCommand(): Command {
    const cmd = new Command('http')
        .description('Make HTTP request with formatted output')
        .argument('<url>', 'Target URL')
        .option('-X, --method <method>', 'HTTP method', 'GET')
        .option('-H, --header <header...>', 'Request headers (repeatable)')
        .option('-d, --data <data>', 'Request body (JSON string)')
        .option('--file <path>', 'Read body from file')
        .option('--timeout <ms>', 'Request timeout in milliseconds', '30000')
        .option('--no-follow', 'Do not follow redirects')
        .option('--only-headers', 'Show only response headers')
        .option('--only-body', 'Show only response body')
        .addHelpText('after', `
Examples:
  $ jai1 utils http https://api.example.com/users
  $ jai1 utils http https://api.example.com/users -X POST -d '{"name":"John"}'
  $ jai1 utils http https://api.example.com -H "Authorization: Bearer token123"
  $ jai1 utils http https://api.example.com -H "Content-Type: application/json" -H "Accept: application/json"
  $ jai1 utils http https://api.example.com --timeout 5000
  $ jai1 utils http https://api.example.com --only-body
`)
        .showHelpAfterError('(add --help for additional examples)')
        .action(async (url, options) => {
            await handleHttpRequest(url, {
                ...options,
                timeout: parseInt(options.timeout, 10),
            });
        });

    return cmd;
}
